import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Pencil, Plus, Trash2, Wand2 } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@monorepo-apps/ui/src/components/sheet";

import {
  useCreateWeightsPreset,
  useDeleteWeightsPreset,
  useDuplicateWeightsPreset,
  useSetDefaultWeightsPreset,
  useUpdateWeightsPreset,
} from "@/application/use-cases/products/use-weights.queries";
import {
  effectiveWeights,
  normalizeAll,
  sumWeights,
} from "@/application/use-cases/products/normalize-weights";
import {
  CRITERION_GROUP_LABEL,
  CRITERION_GROUP_ORDER,
  colorForGroup,
} from "@/presentation/data/criteria";
import type {
  EvaluationCriterionRow,
  WeightsPresetRow,
} from "@/infrastructure/supabase/database.types";

interface WeightsPresetControlProps {
  criteria: EvaluationCriterionRow[];
  presets: WeightsPresetRow[];
  active: WeightsPresetRow | null;
}

/**
 * Interactive weights control on /products: switch the active preset (a global
 * lens applied to every product), and open the editor to tune / manage presets.
 */
export function WeightsPresetControl({ criteria, presets, active }: WeightsPresetControlProps) {
  const setDefault = useSetDefaultWeightsPreset();
  const [editorOpen, setEditorOpen] = useState(false);

  const topWeights = useMemo(() => {
    const w = active?.weights ?? {};
    return criteria
      .map((c) => ({
        slug: c.slug,
        label: c.name,
        weight: w[c.slug] ?? c.weight_default,
        color: colorForGroup(c.group_key),
      }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 6);
  }, [criteria, active]);

  return (
    <aside className="glow-card flex flex-col gap-4 p-5">
      <div className="flex flex-col gap-2">
        <span className="eyebrow">Weights preset</span>
        <Select
          value={active?.id ?? ""}
          onValueChange={(id) => {
            if (id && id !== active?.id) setDefault.mutate(id);
          }}
        >
          <SelectTrigger className="h-9 text-[13px]">
            <SelectValue placeholder="Select preset" />
          </SelectTrigger>
          <SelectContent>
            {presets.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {active?.description ? (
          <p className="text-foreground/60 text-[11.5px] leading-snug">{active.description}</p>
        ) : null}
        <p className="text-muted-foreground text-[10.5px]">
          Active lens — applied to every product & candidate.
        </p>
      </div>

      <Button
        size="sm"
        variant="outline"
        className="w-fit gap-1.5"
        onClick={() => setEditorOpen(true)}
        disabled={!active}
      >
        <Pencil className="size-3" />
        Edit &amp; manage weights
      </Button>

      <div className="flex flex-col gap-2">
        <span className="text-muted-foreground text-[10.5px] tracking-[0.12em] uppercase">
          Top weighted
        </span>
        <ul className="flex flex-col gap-1.5">
          {topWeights.map((w) => (
            <li key={w.slug} className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-2 text-[12.5px]">
                <span
                  className="inline-block size-1.5 rounded-full"
                  style={{ background: w.color }}
                />
                <span className="line-clamp-1">{w.label}</span>
              </span>
              <span className="font-data text-foreground/70 text-[11.5px] tabular-nums">
                {(w.weight * 100).toFixed(0)}%
              </span>
            </li>
          ))}
        </ul>
      </div>

      <WeightsEditorSheet
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        criteria={criteria}
        presets={presets}
        active={active}
      />
    </aside>
  );
}

function WeightsEditorSheet({
  open,
  onClose,
  criteria,
  presets,
  active,
}: {
  open: boolean;
  onClose: () => void;
  criteria: EvaluationCriterionRow[];
  presets: WeightsPresetRow[];
  active: WeightsPresetRow | null;
}) {
  const update = useUpdateWeightsPreset();
  const create = useCreateWeightsPreset();
  const duplicate = useDuplicateWeightsPreset();
  const remove = useDeleteWeightsPreset();
  const setDefault = useSetDefaultWeightsPreset();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [working, setWorking] = useState<Record<string, number>>({});

  // Reset the working copy whenever the active preset changes or the sheet opens.
  useEffect(() => {
    if (!active) return;
    setName(active.name);
    setDescription(active.description ?? "");
    setWorking(effectiveWeights(criteria, active.weights));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, open]);

  const sum = sumWeights(working);
  const sumPct = Math.round(sum * 100);
  const balanced = Math.abs(sum - 1) < 0.005;

  const grouped = useMemo(() => {
    const m = new Map<string, EvaluationCriterionRow[]>();
    for (const c of criteria) {
      const arr = m.get(c.group_key) ?? [];
      arr.push(c);
      m.set(c.group_key, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.display_order - b.display_order);
    return m;
  }, [criteria]);

  const handleSave = () => {
    if (!active) return;
    update.mutate(
      { id: active.id, patch: { name, description: description || null, weights: working } },
      { onSuccess: onClose }
    );
  };

  const handleNew = () => {
    // Create a fresh preset cloned from the current baseline (weight_default).
    const base = effectiveWeights(criteria, {});
    const suffix = Math.random().toString(36).slice(2, 6);
    create.mutate({
      slug: `preset-${suffix}`,
      name: "New preset",
      description: null,
      weights: base,
    });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>Weights presets</SheetTitle>
          <SheetDescription>
            The active preset is a global lens — its weights score every product and candidate.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          {/* Preset roster */}
          <div className="flex flex-col gap-2">
            <span className="text-muted-foreground text-[10.5px] tracking-[0.12em] uppercase">
              Presets
            </span>
            <div className="flex flex-wrap gap-1.5">
              {presets.map((p) => {
                const isActive = p.id === active?.id;
                return (
                  <span
                    key={p.id}
                    className="inline-flex items-center gap-1 rounded-full border py-1 pr-1 pl-2.5 text-[12px] transition-colors"
                    style={
                      isActive
                        ? {
                            borderColor: "oklch(0.55 0.17 190)",
                            background: "oklch(0.95 0.05 190)",
                            color: "oklch(0.35 0.17 190)",
                          }
                        : { borderColor: "oklch(0.91 0.008 240)", color: "oklch(0.45 0.01 240)" }
                    }
                  >
                    <button
                      type="button"
                      onClick={() => !isActive && setDefault.mutate(p.id)}
                      className="inline-flex items-center gap-1"
                      title={isActive ? "Active preset" : "Set active"}
                    >
                      {isActive ? <Check className="size-3" /> : null}
                      {p.name}
                    </button>
                    {!isActive ? (
                      <button
                        type="button"
                        onClick={() => remove.mutate(p.id)}
                        className="hover:text-foreground inline-flex size-4 items-center justify-center rounded-full opacity-60 hover:opacity-100"
                        title="Delete preset"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    ) : null}
                  </span>
                );
              })}
              <button
                type="button"
                onClick={handleNew}
                className="text-foreground/60 hover:text-foreground inline-flex items-center gap-1 rounded-full border border-dashed px-2.5 py-1 text-[12px]"
                style={{ borderColor: "oklch(0.85 0.008 240)" }}
              >
                <Plus className="size-3" />
                New
              </button>
            </div>
            <p className="text-muted-foreground text-[10.5px]">
              Click a preset to make it active. The active one (cyan) can&apos;t be deleted — switch
              away first.
            </p>
          </div>

          {active ? (
            <>
              {/* Rename + duplicate */}
              <div className="flex items-end gap-2">
                <div className="flex flex-1 flex-col gap-1">
                  <span className="text-muted-foreground text-[10.5px] tracking-[0.12em] uppercase">
                    Name
                  </span>
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5"
                  onClick={() => duplicate.mutate(active.id)}
                  title="Duplicate this preset"
                >
                  <Copy className="size-3" />
                  Duplicate
                </Button>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-[10.5px] tracking-[0.12em] uppercase">
                  Description
                </span>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What this lens optimises for…"
                  className="h-9"
                />
              </div>

              {/* Weight editor, grouped */}
              <div className="flex flex-col gap-4">
                {CRITERION_GROUP_ORDER.map((group) => {
                  const rows = grouped.get(group);
                  if (!rows || rows.length === 0) return null;
                  return (
                    <div key={group} className="flex flex-col gap-1.5">
                      <span className="eyebrow flex items-center gap-2">
                        <span
                          className="inline-block size-1.5 rounded-full"
                          style={{ background: colorForGroup(group) }}
                        />
                        {CRITERION_GROUP_LABEL[group]}
                      </span>
                      {rows.map((c) => (
                        <div key={c.id} className="flex items-center gap-3">
                          <span className="flex-1 truncate text-[12.5px]">{c.name}</span>
                          <div className="relative w-20">
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              value={Math.round((working[c.slug] ?? 0) * 100)}
                              onChange={(e) => {
                                const pct = Math.max(0, Math.min(100, Number(e.target.value)));
                                setWorking((prev) => ({ ...prev, [c.slug]: pct / 100 }));
                              }}
                              className="h-8 pr-6 text-right text-[12px] tabular-nums"
                            />
                            <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-[11px]">
                              %
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>

        {active ? (
          <SheetFooter className="border-t px-6 py-4">
            <div className="flex w-full items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span
                  className="font-data text-[12px] font-semibold tabular-nums"
                  style={{ color: balanced ? "oklch(0.45 0.16 145)" : "oklch(0.55 0.16 70)" }}
                >
                  Σ {sumPct}%
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5"
                  onClick={() => setWorking((prev) => normalizeAll(prev))}
                  disabled={balanced}
                  title="Rescale all weights to sum to 100%"
                >
                  <Wand2 className="size-3" />
                  Normalize
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={update.isPending}>
                  {update.isPending ? "Saving…" : "Save weights"}
                </Button>
              </div>
            </div>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
